import React, {
  ForwardRefRenderFunction,
  RefObject,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { AxiosError } from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import UploadCSVFile, { UploadCSVErrors } from "../../../../../../components/Generic/UploadCSVFile";
import { ValidateCSVFieldErrors } from "../../../../../../components/Generic/UploadCSVFile/types";
import { useAppState } from "../../../../../../context/StateContext";
import { Archive } from "../../../../../../models/archive";
import { Error } from "../../../../../../models/error";
import { InvoicesCharges } from "../../../../../../models/invoice.charges";
import { InvoicesShipment } from "../../../../../../models/invoices.shipment";
import { getBase64 } from "../../../../../../util/base64";
import { CodeError } from "../../../../../../util/enums";
import env from "../../../../../../util/env";
import readCSV from "../../../../../../util/readCSV";
import useAction from "../../hooks/useAction";
import { InvoicesTemplateCSV, UploadCsvError } from "../../utils";
import * as ValidateCSV from "./csv.validate";

interface Props {
  ref: RefObject<UploadCSVFileHandles>;
  refetchInvoices: () => void;
}

export interface UploadCSVFileHandles {
  open: (uuid?: string) => void;
  close: () => void;
}

const UploadCSVInvoices: ForwardRefRenderFunction<UploadCSVFileHandles, Props> = (props, ref) => {
  const { refetchInvoices } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const { useSaveInvoices } = useAction();

  const modalRef = useRef<UploadCSVFileHandles>(null);

  const [lineErrorsCSV, setLineErrorsCSV] = useState<UploadCSVErrors>({});
  const [generalErrorsCSV, setGeneralErrorsCSV] = useState<UploadCSVErrors>({});
  const [hasBeenSubmited, setHasBeenSubmited] = useState<boolean>(false);
  const [requestUuid, setRequestUuid] = useState<string>();

  const openModal = useCallback((uuid?: string) => {
    setRequestUuid(uuid);
    modalRef.current?.open();
  }, []);
  const closeModal = useCallback(() => {
    modalRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const { mutate: onSaveInvoices, isLoading } = useSaveInvoices(() => {
    closeModal();
    if (refetchInvoices) refetchInvoices();
  });
  const onConfirmUploadCSV = async (data: InvoicesTemplateCSV[], fileUploaded?: File) => {
    if (hasBeenSubmited) return;

    if (!data || !data.length) {
      NotificationToast.warning(translate("thereAreNoRecordsInThisFile"), {
        id: "warning-file-empty",
      });
    }
    if (!data || !data.length) {
      setHasBeenSubmited(false);

      return;
    }

    const lineErrors: ValidateCSVFieldErrors = {};
    const csvGlobalErrors: UploadCSVErrors = {};

    const { valid } = ValidateCSV.validateCSVFields(data, lineErrors, translate);
    const invoices: InvoicesShipment[] = [];
    const shipments = new Map<string, number>();

    if (valid) {
      data.forEach((dataItem) => {
        if (!dataItem?.poNumber) return;

        const charge: InvoicesCharges = {
          chargeCode: dataItem?.chargeCode,
          description: dataItem?.description,
          overseasAmount: Number(dataItem?.overseasAmount.trim()),
          exchangeRateCurrency: dataItem?.exchangeRateCurrency,
          invoicedLocalAmount: Number(dataItem?.invoicedLocalAmount),
          gstAmount: Number(dataItem?.gstAmount.trim()),
          exchangeRate: dataItem?.exchangeRate ? Number(dataItem?.exchangeRate) : 1,
          invoiceNumber: dataItem?.invoiceNumber,
          invoiceDate: new Date(dataItem?.invoiceDate),
          invoiceDueDate: new Date(dataItem?.invoiceDueDate),
          autoRate: dataItem?.autoRate,
          cottonOnAdultsApportionment: dataItem?.cottonOnAdultsApportionment
            ? Number(dataItem?.cottonOnAdultsApportionment)
            : undefined,
          cottonOnAdultsLocalAmount: dataItem?.cottonOnAdultsLocalAmount
            ? Number(dataItem?.cottonOnAdultsLocalAmount)
            : undefined,
          ceresLifeApportionment: dataItem?.ceresLifeApportionment
            ? Number(dataItem?.ceresLifeApportionment)
            : undefined,
          ceresLifeLocalAmount: dataItem?.ceresLifeLocalAmount
            ? Number(dataItem?.ceresLifeLocalAmount)
            : undefined,
          bodyApportionment: dataItem?.bodyApportionment
            ? Number(dataItem?.bodyApportionment)
            : undefined,
          bodyLocalAmount: dataItem?.bodyLocalAmount
            ? Number(dataItem?.bodyLocalAmount)
            : undefined,
          factorieApportionment: dataItem?.factorieApportionment
            ? Number(dataItem?.factorieApportionment)
            : undefined,
          factorieLocalAmount: dataItem?.factorieLocalAmount
            ? Number(dataItem?.factorieLocalAmount)
            : undefined,
          kidsApportionment: dataItem?.kidsApportionment
            ? Number(dataItem?.kidsApportionment)
            : undefined,
          kidsLocalAmount: dataItem?.kidsLocalAmount
            ? Number(dataItem?.kidsLocalAmount)
            : undefined,
          rubiApportionment: dataItem?.rubiApportionment
            ? Number(dataItem?.rubiApportionment)
            : undefined,
          rubiLocalAmount: dataItem?.rubiLocalAmount
            ? Number(dataItem?.rubiLocalAmount)
            : undefined,
          foundationApportionment: dataItem?.foundationApportionment
            ? Number(dataItem?.foundationApportionment)
            : undefined,
          foundationLocalAmount: dataItem?.foundationLocalAmount
            ? Number(dataItem?.foundationLocalAmount)
            : undefined,
          typoApportionment: dataItem?.typoApportionment
            ? Number(dataItem?.typoApportionment)
            : undefined,
          typoLocalAmount: dataItem?.typoLocalAmount
            ? Number(dataItem?.typoLocalAmount)
            : undefined,
          gamedayApportionment: dataItem?.gamedayApportionment
            ? Number(dataItem?.gamedayApportionment)
            : undefined,
          gamedayLocalAmount: dataItem?.gamedayLocalAmount
            ? Number(dataItem?.gamedayLocalAmount)
            : undefined,
          supreApportionment: dataItem?.supreApportionment
            ? Number(dataItem?.supreApportionment)
            : undefined,
          supreLocalAmount: dataItem?.supreLocalAmount
            ? Number(dataItem?.supreLocalAmount)
            : undefined,
          fixturesApportionment: dataItem?.fixturesApportionment
            ? Number(dataItem?.fixturesApportionment)
            : undefined,
          fixturesLocalAmount: dataItem?.fixturesLocalAmount
            ? Number(dataItem?.fixturesLocalAmount)
            : undefined,
          consumablesApportionment: dataItem?.consumablesApportionment
            ? Number(dataItem?.consumablesApportionment)
            : undefined,
          consumablesLocalAmount: dataItem?.consumablesLocalAmount
            ? Number(dataItem?.consumablesLocalAmount)
            : undefined,
          otherApportionment: dataItem?.otherApportionment
            ? Number(dataItem?.otherApportionment)
            : undefined,
          otherLocalAmount: dataItem?.otherLocalAmount
            ? Number(dataItem?.otherLocalAmount)
            : undefined,
          companyId: currentUser?.companyId,
        };

        if (shipments.has(dataItem?.shipment)) {
          const index = Number(shipments.get(dataItem?.shipment));

          invoices[index] = {
            ...invoices[index],
            invoices: [...(invoices[index].invoices || []), charge],
          };
        } else {
          const invoiceShipment: InvoicesShipment = {
            shipment: dataItem?.shipment,
            poNumber: dataItem?.poNumber,
            partNumber: dataItem?.partNumber,
            origin: dataItem?.origin,
            originCountry: dataItem?.country,
            destination: dataItem?.destination,
            etd: dataItem?.etd ? moment(dataItem?.etd?.trim(), "DD/MM/YYYY").toDate() : undefined,
            eta: dataItem?.etd ? moment(dataItem?.eta.trim(), "DD/MM/YYYY").toDate() : undefined,
            mode: dataItem?.mode,
            container: dataItem?.container,
            supplierName: dataItem?.supplierName,
            houseBill: dataItem?.houseBill,
            masterBill: dataItem?.masterBill,
            containers: dataItem?.containers,
            weight: Number(dataItem?.weight),
            teu: dataItem?.teu ? Number(dataItem?.teu) : undefined,
            cbm: Number(dataItem?.cbm),
            cartons: Number(dataItem?.cartons),
            hblMode: dataItem?.hblMode,
            delivered: dataItem?.delivered
              ? moment(dataItem?.delivered?.trim(), "DD/MM/YYYY").toDate()
              : undefined,
            invoices: [charge],
          };

          shipments.set(dataItem?.shipment, invoices.length);
          invoices.push(invoiceShipment);
        }
      });
    }
    if (!valid || !!Object.keys(csvGlobalErrors).length) {
      setLineErrorsCSV(lineErrors);
      setGeneralErrorsCSV(csvGlobalErrors);
      setHasBeenSubmited(false);

      return;
    }

    if (fileUploaded) {
      const uri = await getBase64<string>(fileUploaded, "dataURL");
      const archive: Archive = {
        filename: fileUploaded.name,
        fileType: "csv",
        uri,
      };

      onSaveInvoices(
        { invoices, archive, uuid: requestUuid || "" },
        {
          onError: (error) => {
            const errorAxios = error as AxiosError<Error>;

            setHasBeenSubmited(false);

            if (
              errorAxios?.response?.data &&
              errorAxios.response.data.error === CodeError.INVALID_CSV
            ) {
              parseSaveInvoiceErrors(errorAxios.response.data.message);
            }
          },
        }
      );
    }
  };

  const errorMessage = useCallback(
    (message: string, type: string, value: string) =>
      `${translate(message)} ${translate(type)} ${value}`,
    []
  );

  const parseSaveInvoiceErrors = (errorString?: string) => {
    const errors: UploadCsvError[] = errorString ? JSON.parse(errorString) : [];

    const generalErrors: UploadCSVErrors = {};

    for (const error of errors) {
      if (!generalErrors[error.column]) generalErrors[error.column] = [];
      generalErrors[error.column].push(errorMessage(error.translate, error.type, error.column));
    }

    setGeneralErrorsCSV(generalErrors);
  };

  return (
    <UploadCSVFile
      ref={modalRef}
      onConfirmUpload={(uri, fileUploaded) => {
        setHasBeenSubmited(true);
        readCSV<InvoicesTemplateCSV>(
          uri,
          [
            "shipment",
            "poNumber",
            "partNumber",
            "origin",
            "country",
            "destination",
            "etd",
            "eta",
            "mode",
            "container",
            "supplierName",
            "houseBill",
            "masterBill",
            "containers",
            "weight",
            "teu",
            "cbm",
            "cartons",
            "hblMode",
            "chargeCode",
            "description",
            "overseasAmount",
            "exchangeRateCurrency",
            "exchangeRate",
            "invoicedLocalAmount",
            "gstAmount",
            "invoiceNumber",
            "invoiceDate",
            "invoiceDueDate",
            "delivered",
            "autoRate",
            "cottonOnAdultsApportionment",
            "cottonOnAdultsLocalAmount",
            "ceresLifeApportionment",
            "ceresLifeLocalAmount",
            "bodyApportionment",
            "bodyLocalAmount",
            "factorieApportionment",
            "factorieLocalAmount",
            "kidsApportionment",
            "kidsLocalAmount",
            "rubiApportionment",
            "rubiLocalAmount",
            "foundationApportionment",
            "foundationLocalAmount",
            "typoApportionment",
            "typoLocalAmount",
            "gamedayApportionment",
            "gamedayLocalAmount",
            "supreApportionment",
            "supreLocalAmount",
            "fixturesApportionment",
            "fixturesLocalAmount",
            "consumablesApportionment",
            "consumablesLocalAmount",
            "otherApportionment",
            "otherLocalAmount",
          ],
          (data) => onConfirmUploadCSV(data, fileUploaded),
          {
            uuid: true,
          }
        );
      }}
      lineErrors={lineErrorsCSV}
      generalErrors={generalErrorsCSV}
      onCloseModal={() => {
        setLineErrorsCSV({});
        setGeneralErrorsCSV({});
        setHasBeenSubmited(false);
        setRequestUuid("");
      }}
      onDownloadTemplate={() =>
        window.open(env.LINK_TEMPLATE_UPLOAD_CARTONS_CSV, "_blank")?.focus()
      }
      loadingUpload={isLoading}
    />
  );
};

export default React.forwardRef(UploadCSVInvoices);
